import React, { FC, useState, useRef, useEffect, useCallback } from 'react'
import { useUserSelector } from '../../store/selectors/userSelector'
import { useLanguageSelector } from '../../store/selectors/fpLoaderSelector'
import { PiBookOpenBold, PiCheckCircleFill } from 'react-icons/pi'
import { useChatToExperimentApi } from '../../store/api/hooks/chatToExperimentApiHook'
import { VscSend } from 'react-icons/vsc'
import Loader from '../loader'
import Md from './components/md'
import UploadData from '../../pages/CreateExperiment/setScope/_components/uploadData/uploadData'
import ProcessData from '../../pages/CreateExperiment/setScope/_components/processData/ProcessData'
import CustomAgGrid from '../CustomAgGrid'
import RCTUploadData from '../../pages/Measurement/Data/_components/uploadData/RCT/uploadData'
import MeasurementProcessData from '../../pages/Measurement/Data/_components/processData/ProcessData'
import moment from 'moment'
import { trackEvent } from '../../_common/functions'
import { IoIosArrowDown, IoIosArrowUp, IoMdMic } from 'react-icons/io'
import { ReactComponent as LogoIcon } from '../../assets/svg/logoicon.svg'
import { useSpeechToText } from '../../_common/hook/useSpeechToText'
import { useExperimentApi } from '../../store/api/hooks/experimentApiHook'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import pusherService from '../../services/pusherService'
import { APP_ENV } from '../../_config'
import { decompressAndDownload } from '../../pages/CreateExperiment/ConfigureGroups/_components/allocation'

interface Message {
    id: string
    text: string
    sender: 'user' | 'bot'
    timestamp: number | string
    isLoading?: boolean
    loadingText?: string
    uiPayload?: {
        type:
            | 'summary_card'
            | 'group_config_card'
            | 'design_actions_card'
            | 'measurement_config'
            | 'group_config'
            | 'design_actions'
            | 'data_upload_for_design'
            | 'filter_for_design'
            | 'data_upload_for_measurement'
            | 'filter_for_measurement'
            | 'analysis_triggered'
            | 'data_preview_for_design'
            | 'data_preview_for_measurement'
            | 'applied_sql'
        data: any
    }
    attachment?: {
        name: string
        size: string
        type: string
    }
    options?: string[]
    uploadButton?: boolean
    buttons?: Record<string, string>
}

interface Step {
    id: string
    label: string
    status: 'completed' | 'active' | 'pending'
}

interface ChatToExperimentProps {
    _ref?: any
    initialSteps?: Step[] | null
    initialMessages?: Message[]
    initialSessionId?: string | null
    onSendMessage?: (message: string) => void
    onUploadFile?: (file: File) => void
}

const getDefaultSteps = (t: (key: string) => string): Step[] => [
    { id: 'create_experiment', label: t('Design Experiment'), status: 'active' },
    { id: 'data_upload', label: t('Set Scope'), status: 'pending' },
    { id: 'set_experiment_configuration', label: t('Configure Groups'), status: 'pending' },
    { id: 'group_allocation_complete', label: t('Design Complete'), status: 'pending' },
    { id: 'measurement_configuration', label: t('Measurements'), status: 'pending' },
]

const STEP_LABEL_KEYS: Record<string, string> = {
    create_experiment: 'Design Experiment',
    data_upload: 'Set Scope',
    set_experiment_configuration: 'Configure Groups',
    group_allocation_complete: 'Design Complete',
    measurement_configuration: 'Measurements',
}

const formatTime = () => {
    const now = new Date()
    return `Today, ${now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
}

const getCharsPerTick = (len: number): number => {
    if (len <= 200) return 1
    if (len <= 500) return 2
    if (len <= 1000) return 4
    return Math.ceil(len / 250)
}

const replaceMessage = (messages: Message[], targetId: string, updates: Partial<Message>): Message[] => {
    return messages.map((m) => (m.id === targetId ? { ...m, ...updates } : m))
}

const getStepLineClass = (status: string): string => {
    if (status === 'active') return 'border-[#CD9F27]'
    // "skipped" (not applicable for this flow, e.g. Measurement-Only) must render the same as "pending"
    if (status === 'pending' || status === 'skipped') return 'border-slate-300 border-dashed'
    return 'border-green-700'
}

const getStepIconClass = (status: string): string => {
    if (status === 'active') return 'text-[#CD9F27]'
    if (status === 'pending' || status === 'skipped') return 'text-slate-300'
    return 'text-green-700'
}

/**
 * Standalone, persistent card for a freshly applied NL-to-SQL filter result.
 * Rendered inline in the conversation (not a modal) so the user can scroll back
 * and review it at any time, and must reply before the next recommendation card appears.
 */
const DataPreviewCard: FC<{ data: any }> = ({ data }) => {
    const { t } = useTranslation()
    const gridRef = useRef<any>(null)
    const rows = Array.isArray(data?.rows) ? data.rows : []
    const totalRows = data?.total_rows ?? rows.length
    const rowCountLabel = Number(totalRows).toLocaleString()
    const columnNames = Array.from(new Set(rows.flatMap((row: Record<string, any>) => Object.keys(row || {}))))
    const columnDefs = columnNames.map((field) => ({ field, headerName: field, minWidth: 130 }))

    return (
        <div className="border border-gray-200 rounded-[0.833rem] bg-white mt-3 w-full max-w-5xl overflow-hidden shadow-sm" data-testid="nl2sql-filtered-data-preview-card">
            <div className="relative">
                <span className="bg-[#EBEFF1] absolute left-0 top-0 h-14 w-full z-0" />
                <div className="px-4 pt-3 pb-2 relative z-[1]">
                    <div className="text-xs text-gray-500">{t('Filtered Data Preview')}</div>
                    <div className="text-sm font-bold text-gray-900 mt-1">
                        {data?.table_name || t('Data Preview')} ({rowCountLabel} {t('rows')})
                    </div>
                </div>
            </div>
            <div className="px-4 pb-4 pt-1">
                {rows.length > 0 ? (
                    <div className="ag-theme-alpine">
                        <CustomAgGrid
                            gridRef={gridRef}
                            rowData={rows}
                            columnDefs={columnDefs as any}
                            defaultColDef={{ sortable: false, filter: false, resizable: true }}
                            pagination={false}
                            style={{ height: 400, width: '100%' }}
                            theme="light"
                            dataTestId="nl2sql-filtered-data-preview-grid"
                        />
                    </div>
                ) : (
                    <p className="text-xs text-gray-500 m-0">{t('No rows match the finalized filter.')}</p>
                )}
            </div>
        </div>
    )
}

/**
 * Standalone card showing the raw SQL of the currently-applied NL-to-SQL filter.
 * The SQL arrives only via ui_payload (never through the LLM/Asimov path), so it is safe to display.
 */
const AppliedSqlCard: FC<{ data: any }> = ({ data }) => {
    const { t } = useTranslation()
    const sql = typeof data?.raw_sql === 'string' ? data.raw_sql : ''

    if (!sql) {
        return null
    }

    return (
        <div className="border border-gray-200 rounded-[0.833rem] bg-white mt-3 w-full max-w-5xl overflow-hidden shadow-sm" data-testid="nl2sql-applied-sql-card">
            <div className="relative">
                <span className="bg-[#EBEFF1] absolute left-0 top-0 h-14 w-full z-0" />
                <div className="px-4 pt-3 pb-2 relative z-[1]">
                    <div className="text-xs text-gray-500">{t('Applied Filter SQL')}</div>
                    <div className="text-sm font-bold text-gray-900 mt-1">
                        {t(data?.stage || 'Design')} {t('filter')}
                        {data?.version ? ` (v${data.version})` : ''}
                    </div>
                </div>
            </div>
            <div className="px-4 pb-4 pt-1">
                <pre className="text-xs text-gray-800 bg-[#F7F9FA] border border-gray-200 rounded-md p-3 overflow-auto m-0 whitespace-pre-wrap break-words" data-testid="nl2sql-applied-sql-text">
                    {sql}
                </pre>
            </div>
        </div>
    )
}

const SummaryCard: FC<{ data: any }> = ({ data }) => {
    const { t } = useTranslation()
    const [showMetrics, setShowMetrics] = useState(true)
    const [showGroups, setShowGroups] = useState(true)
    return (
        <div className="border border-gray-200 rounded-[0.833rem] bg-white mt-3 w-full max-w-2xl overflow-hidden shadow-sm">
            {/* Header */}
            <div className="relative">
                <span className="bg-[#EBEFF1] absolute left-0 top-0 h-16 w-full z-0" />
                <div className="px-4 pt-3 pb-2 relative z-[1]">
                    <div className="text-xs text-gray-500">{t('Experiment Summary')}</div>
                    <div className="flex justify-between items-center mt-1">
                        <div className="text-sm font-bold text-gray-900">{data.experiment_name}</div>
                        {data.experiment_type && (
                            <div className="flex items-center bg-white rounded-lg px-2 py-0.5 text-xs">
                                <span className="text-gray-500">{t('Expt. Type')} :</span>&nbsp;
                                <span className="font-semibold">{typeof data.experiment_type === 'string' ? data.experiment_type : data.experiment_type?.title}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="px-4 pb-4">
                {/* Context */}
                <div className="bg-white rounded-md py-2.5 px-3 mt-1 shadow-[0_1px_4px_rgba(139,134,134,0.3)]">
                    <div className="text-xs font-semibold text-gray-800">{t('Context')}</div>
                    {data.problem_statement && (
                        <div className="mt-1.5">
                            <div className="text-[11px] font-semibold text-gray-700">{t('Problem Statement')} :</div>
                            <div className="text-[11px] text-gray-600 mt-0.5">{data.problem_statement}</div>
                        </div>
                    )}
                    <div className="flex justify-between mt-2 gap-3">
                        <div className="flex flex-col gap-1.5 flex-1">
                            {data.use_case && (
                                <div>
                                    <div className="text-[11px] font-semibold text-gray-700">{t('Use Case')} :</div>
                                    <div className="text-[11px] text-gray-600">{typeof data.use_case === 'string' ? data.use_case : data.use_case?.title}</div>
                                </div>
                            )}
                            {data.hypothesis && (
                                <div>
                                    <div className="text-[11px] font-semibold text-gray-700">{t('Hypothesis')} :</div>
                                    <div className="text-[11px] text-gray-600">{data.hypothesis}</div>
                                </div>
                            )}
                        </div>
                        {(data.business_function || data.country || data.start_date) && (
                            <>
                                <div className="w-px bg-[#D9D9D9] self-stretch" />
                                <div className="flex flex-col gap-1 flex-shrink-0">
                                    <div className="text-[11px] font-semibold text-gray-700">{t('Information')} :</div>
                                    {data.business_function && (
                                        <div className="flex items-center justify-between gap-4 text-[11px]">
                                            <span className="text-gray-600">{t('Business Function')} :</span>
                                            <span className="font-semibold text-gray-800">{typeof data.business_function === 'string' ? data.business_function : data.business_function?.title}</span>
                                        </div>
                                    )}
                                    {data.country && (
                                        <div className="flex items-center justify-between gap-4 text-[11px]">
                                            <span className="text-gray-600">{t('Country')} :</span>
                                            <span className="font-semibold text-gray-800">{typeof data.country === 'string' ? data.country : data.country?.title}</span>
                                        </div>
                                    )}
                                    {(data.start_date || data.end_date) && (
                                        <div className="flex items-center justify-between gap-4 text-[11px]">
                                            <span className="text-gray-600">{t('Date')} :</span>
                                            <span className="font-semibold text-gray-800">
                                                {moment(data.start_date, 'YYYY-MM-DD').format('YYYY/MM/DD')} &nbsp;&mdash;&nbsp; {moment(data.end_date, 'YYYY-MM-DD').format('YYYY/MM/DD')}
                                            </span>
                                        </div>
                                    )}
                                    {(data.baseline_start || data.baseline_end) && (
                                        <div className="flex items-center justify-between gap-4 text-[11px]">
                                            <span className="text-gray-600">{t('Baseline')} :</span>
                                            <span className="font-semibold text-gray-800">
                                                {moment(data.baseline_start, 'YYYY-MM-DD').format('YYYY/MM/DD')} &nbsp;&mdash;&nbsp; {moment(data.baseline_end, 'YYYY-MM-DD').format('YYYY/MM/DD')}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Metrics & Success Criteria */}
                {data.metrices?.length > 0 && (
                    <div className="mt-3">
                        <button
                            type="button"
                            tabIndex={0}
                            className="flex justify-between items-center cursor-pointer w-full bg-transparent border-none p-0"
                            onClick={() => {
                                trackEvent('ai_summary_metrics_toggled', { expanded: !showMetrics })
                                setShowMetrics(!showMetrics)
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    trackEvent('ai_summary_metrics_toggled', { expanded: !showMetrics })
                                    setShowMetrics(!showMetrics)
                                }
                            }}
                        >
                            <span className="text-xs font-semibold text-gray-800">{t('Metrics & Success Criteria')} :</span>
                            <span className="text-gray-400 text-sm">{showMetrics ? <IoIosArrowUp /> : <IoIosArrowDown />}</span>
                        </button>
                        {showMetrics && (
                            <div className="mt-2">
                                <div className="text-[11px] font-semibold text-gray-700 mb-1">{t('Metrics')}:</div>
                                <div className="rounded-md border overflow-hidden">
                                    <div className="grid grid-cols-4">
                                        <div className="bg-[#ECECEC] border-r flex justify-center py-1">
                                            <span className="text-[11px] font-semibold">{t('KPI of Interest')}</span>
                                        </div>
                                        <div className="bg-[#ECECEC] flex justify-center py-1">
                                            <span className="text-[11px] font-semibold">{t('KPI Type')}</span>
                                        </div>
                                        <div className="bg-[#ECECEC] flex justify-center py-1">
                                            <span className="text-[11px] font-semibold">{t('KPI Unit')}</span>
                                        </div>
                                        <div className="bg-[#ECECEC] flex justify-center py-1">
                                            <span className="text-[11px] font-semibold">{t('Minimum Uplift for Success')}</span>
                                        </div>
                                    </div>
                                    {data.metrices.map((m: any, idx: number) => (
                                        <div key={m.literral_value} className={`grid grid-cols-4 ${idx % 2 === 1 ? 'bg-[#FDFAEE]' : ''}`}>
                                            <div className="flex justify-center py-1 text-[11px]">{m.literral_value}</div>
                                            <div className="flex justify-center py-1 text-[11px]">{m.kpi_type}</div>
                                            <div className="flex justify-center py-1 text-[11px]">{m.kpi_unit}</div>
                                            <div className="flex justify-center py-1 text-[11px]">{m.minimum_uplift}%</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Group Details */}
                {data.groups?.length > 0 && (
                    <div className="mt-3">
                        <button
                            type="button"
                            tabIndex={0}
                            className="flex justify-between items-center cursor-pointer w-full bg-transparent border-none p-0"
                            onClick={() => {
                                trackEvent('ai_summary_groups_toggled', { expanded: !showGroups })
                                setShowGroups(!showGroups)
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    trackEvent('ai_summary_groups_toggled', { expanded: !showGroups })
                                    setShowGroups(!showGroups)
                                }
                            }}
                        >
                            <span className="text-xs font-semibold text-gray-800">{t('Group Details')} :</span>
                            <span className="text-gray-400 text-sm">{showGroups ? <IoIosArrowUp /> : <IoIosArrowDown />}</span>
                        </button>
                        {showGroups && (
                            <div className="grid grid-cols-2 gap-3 mt-2">
                                {data.groups.map((g: any, idx: number) => (
                                    <div key={g.name} className={`flex items-center justify-between px-2.5 py-2 rounded-md ${idx === 0 ? 'bg-black text-white' : 'bg-[#F5E003] text-gray-900'}`}>
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[11px] font-semibold">{g.name}</span>
                                                {idx === 0 && <span className="bg-[#4B471B] text-[#F5E003] text-[9px] px-1.5 rounded-full">{t('Control Group')}</span>}
                                            </div>
                                            <div className="flex gap-2 text-[10px]">
                                                <div>
                                                    <div className={idx === 0 ? 'text-slate-400' : 'text-gray-600'}>{t('Action Taken')}:</div>
                                                    <div className={`font-medium ${idx === 0 ? 'text-[#F5E003]' : ''}`}>{g.action}</div>
                                                </div>
                                                {g.distribution && (
                                                    <>
                                                        <div className={`border-l ${idx === 0 ? 'border-slate-400' : 'border-gray-400'}`} />
                                                        <div>
                                                            <div className={idx === 0 ? 'text-slate-400' : 'text-gray-600'}>{t('Distribution %')} :</div>
                                                            <div className="font-medium">{g.distribution}%</div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {g.group_size && (
                                            <div className="flex flex-col items-end text-[10px]">
                                                <span className={idx === 0 ? 'text-slate-400' : 'text-gray-500'}>{t('Group Size')}</span>
                                                <span className="text-base font-bold">{g.group_size}</span>
                                                {g.total_size && (
                                                    <span className={idx === 0 ? 'text-slate-400' : 'text-gray-500'}>
                                                        {t('out of')} <span className={idx === 0 ? 'text-slate-200' : 'font-semibold'}>{g.total_size}</span>
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

const MeasurementConfigCard: FC<{ data: any }> = ({ data }) => {
    const { t } = useTranslation()
    const selected = data?.selected || {}
    const availableColumns = data?.available_columns || {}
    const kpiMappings = selected?.kpi_mappings || []
    const blockingFactors = selected?.blocking_factors || []

    const mappedKpiColumns = new Set(kpiMappings.map((kpi: any) => kpi.column_name).filter(Boolean))
    const availableGranularity = (availableColumns.granularity || []).filter(
        (col: string) => col !== selected.granularity && col !== selected.date_col && col !== selected.allocation_group_col && !blockingFactors.includes(col) && !mappedKpiColumns.has(col),
    )
    const availableDate = (availableColumns.date || []).filter((col: string) => col !== selected.date_col && col !== selected.granularity && col !== selected.allocation_group_col)
    const availableAllocationGroup = (availableColumns.allocation_group || []).filter((col: string) => col !== selected.allocation_group_col && col !== selected.granularity && col !== selected.date_col && !blockingFactors.includes(col))
    const availableBlocking = (availableColumns.blocking || []).filter(
        (col: string) => !blockingFactors.includes(col) && col !== selected.granularity && col !== selected.date_col && col !== selected.allocation_group_col && !mappedKpiColumns.has(col),
    )
    const availableKpi = (availableColumns.kpi || []).filter((col: string) => col !== selected.granularity && col !== selected.date_col && col !== selected.allocation_group_col && !mappedKpiColumns.has(col))

    return (
        <div className="border border-gray-200 rounded-[0.833rem] bg-white mt-3 w-full max-w-2xl overflow-hidden shadow-sm">
            {/* Header */}
            <div className="relative">
                <span className="bg-[#EBEFF1] absolute left-0 top-0 h-14 w-full z-0" />
                <div className="px-4 pt-3 pb-2 relative z-[1]">
                    <div className="text-xs text-gray-500">{t('Measurement Configuration')}</div>
                    <div className="text-sm font-bold text-gray-900 mt-1">{t('Data & Mapping Settings')}</div>
                </div>
            </div>

            <div className="px-4 pb-4">
                {/* Selected Configuration */}
                <div className="bg-white rounded-md py-2.5 px-3 mt-1 shadow-[0_1px_4px_rgba(139,134,134,0.3)]">
                    <div className="text-xs font-semibold text-gray-800 mb-2">{t('Configuration')}</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <div>
                            <div className="text-[11px] text-gray-500">{t('Granularity')}</div>
                            <div className="text-[11px] font-semibold text-gray-800">{selected.granularity ?? '—'}</div>
                            {availableGranularity.length > 0 && (
                                <div className="mt-1">
                                    <div className="text-[10px] text-gray-400">{t('Available')}:</div>
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                        {availableGranularity.map((col: string) => (
                                            <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                                {col}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="text-[11px] text-gray-500">{t('Date Column')}</div>
                            <div className="text-[11px] font-semibold text-gray-800">{selected.date_col ?? '—'}</div>
                            {availableDate.length > 0 && (
                                <div className="mt-1">
                                    <div className="text-[10px] text-gray-400">{t('Available')}:</div>
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                        {availableDate.map((col: string) => (
                                            <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                                {col}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        {selected.date_frequency && (
                            <div>
                                <div className="text-[11px] text-gray-500">{t('Date Frequency')}</div>
                                <div className="text-[11px] font-semibold text-gray-800 capitalize">{selected.date_frequency}</div>
                            </div>
                        )}
                        <div>
                            <div className="text-[11px] text-gray-500">{t('Allocation Group')}</div>
                            <div className="text-[11px] font-semibold text-gray-800">{selected.allocation_group_col || '—'}</div>
                            {availableAllocationGroup.length > 0 && (
                                <div className="mt-1">
                                    <div className="text-[10px] text-gray-400">{t('Available')}:</div>
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                        {availableAllocationGroup.map((col: string) => (
                                            <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                                {col}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="mt-3">
                    <div className="text-xs font-semibold text-gray-800 mb-1.5">{t('Blocking Factors')}</div>
                    <div className="flex flex-wrap gap-1.5">
                        {blockingFactors.length > 0 ? (
                            <>
                                {blockingFactors.map((bf: string) => (
                                    <span key={bf} className="bg-[#FDFAEE] border border-[#E8DFB8] text-[11px] text-gray-700 px-2 py-0.5 rounded-full">
                                        {bf}
                                    </span>
                                ))}
                            </>
                        ) : (
                            <div className="text-[11px] text-gray-500">—</div>
                        )}
                    </div>
                </div>
                {/* Blocking Factors */}

                {availableBlocking.length > 0 && (
                    <div className="mt-1.5">
                        <div className="text-[10px] text-gray-400">{t('Available')}:</div>
                        <div className="flex flex-wrap gap-1 mt-0.5">
                            {availableBlocking.map((col: string) => (
                                <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                    {col}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* KPI Mappings */}
                <div className="mt-3">
                    <div className="text-xs font-semibold text-gray-800 mb-1.5">{t('KPI Mappings')}</div>
                    <div className="rounded-md border overflow-hidden">
                        <div className="grid grid-cols-2">
                            <div className="bg-[#ECECEC] border-r flex justify-center py-1">
                                <span className="text-[11px] font-semibold">{t('Metric Name')}</span>
                            </div>
                            <div className="bg-[#ECECEC] flex justify-center py-1">
                                <span className="text-[11px] font-semibold">{t('Column Name')}</span>
                            </div>
                        </div>
                        {kpiMappings?.map((kpi: any, idx: number) => (
                            <div key={kpi.metric_name} className={`grid grid-cols-2 ${idx % 2 === 1 ? 'bg-[#FDFAEE]' : ''}`}>
                                <div className="flex justify-center py-1.5 text-[11px] text-gray-700">{kpi.metric_name}</div>
                                <div className="flex justify-center py-1.5">
                                    <span className="bg-[#F5E003] text-[11px] px-2 py-0.5 rounded-full font-medium">{kpi.column_name}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    {availableKpi.length > 0 && (
                        <div className="mt-1.5">
                            <div className="text-[10px] text-gray-400">{t('Available KPI Columns')}:</div>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                                {availableKpi.map((col: string) => (
                                    <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                        {col}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const GroupConfigCard: FC<{ data: any }> = ({ data }) => {
    const { t } = useTranslation()
    const selected = data?.selected || {}
    const availableColumns = data?.available_columns || {}
    const groupDistributions = data?.group_distributions || []
    const totalSampleSize = data?.total_sample_size
    const blockingFactors = selected?.blocking_factors || []
    const algorithm = data?.algorithm
    const availableTestControlValues =
        data?.group_column_distinct_values?.filter((val: string) => {
            return val !== selected.control_group_label && val !== selected.treatment_group_label
        }) || []

    // OBS experiments carry group_column / control_group_label / kpi_mappings (list) /
    // features / date_col — RCT carries a single kpi_column and % + sample_size per group.
    const isObs = Boolean(algorithm || selected.group_column || selected.kpi_mappings)
    const kpiMappings: any[] = selected?.kpi_mappings || []
    let kpiColumns: string[]
    if (isObs) {
        kpiColumns = kpiMappings.map((m: any) => m.column_map).filter(Boolean)
    } else if (selected.kpi_column) {
        kpiColumns = [selected.kpi_column]
    } else {
        kpiColumns = []
    }
    const features: string[] = selected?.features || []

    const exclude = new Set([selected.granularity, selected.group_column, ...kpiColumns, ...blockingFactors])
    const availableGranularity = (availableColumns.granularity || []).filter((col: string) => !exclude.has(col))
    const availableKpi = (availableColumns.kpi || []).filter((col: string) => !exclude.has(col))
    const availableBlocking = (availableColumns.blocking || []).filter((col: string) => !blockingFactors.includes(col) && !exclude.has(col))
    const availableGroupColumn = (availableColumns.group_column || []).filter((col: string) => col !== selected.group_column)
    const availableFeatures = (availableColumns.features || []).filter((col: string) => !features.includes(col) && !exclude.has(col))

    return (
        <div className="border border-gray-200 rounded-[0.833rem] bg-white mt-3 w-full max-w-2xl overflow-hidden shadow-sm">
            {/* Header */}
            <div className="relative">
                <span className="bg-[#EBEFF1] absolute left-0 top-0 h-14 w-full z-0" />
                <div className="px-2 pt-1.5 pb-2 relative z-[1]">
                    <div className="text-[0.625rem] text-gray-500">{t('Group Configuration')}</div>
                    <div className="flex justify-between items-center">
                        <div className="text-[0.70rem] font-bold text-gray-900">{t('Group & Allocation Settings')}</div>
                        <div className="flex items-center gap-1">
                            {algorithm && (
                                <div className="flex items-center bg-white rounded-lg px-2 py-0.5 text-[0.475rem]">
                                    <span className="text-gray-500">{t('Algorithm')} :</span>&nbsp;
                                    <span className="font-semibold">{algorithm}</span>
                                </div>
                            )}
                            {totalSampleSize && (
                                <div className="flex items-center bg-white rounded-lg px-2 py-0.5 text-[0.475rem]">
                                    <span className="text-gray-500">{t('Total Sample')} :</span>&nbsp;
                                    <span className="font-semibold">{totalSampleSize.toLocaleString()}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="px-4 pb-4">
                {/* Selected Configuration */}
                <div className="bg-white rounded-md py-2.5 px-3 mt-1 shadow-[0_1px_4px_rgba(139,134,134,0.3)]">
                    <div className="text-xs font-semibold text-gray-800 mb-2">{t('Configuration')}</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        <div className="grid grid-cols-1 gap-3">
                            <div>
                                <div className="text-[11px] text-gray-500">{t('Granularity')}</div>
                                <div className="text-[11px] font-semibold text-gray-800">{selected.granularity ?? '—'}</div>
                                {availableGranularity.length > 0 && (
                                    <div className="mt-1">
                                        <div className="text-[10px] text-gray-400">{t('Available')}:</div>
                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                            {availableGranularity.map((col: string) => (
                                                <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                                    {col}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* OBS-only: Date column / frequency (DTW) */}
                            {isObs && (
                                <div>
                                    <div className="text-[11px] text-gray-500">{t('Date Column')}</div>
                                    <div className="text-[11px] font-semibold text-gray-800">{selected.date_col ?? '—'}</div>
                                    {selected.date_frequency && (
                                        <div className="text-[10px] text-gray-400 mt-0.5">
                                            {t('Frequency')}: {selected.date_frequency}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div>
                                <div className="text-[11px] text-gray-500">{kpiColumns.length > 1 ? t('KPI Columns') : t('KPI Column')}</div>
                                <div className="text-[11px] font-semibold text-gray-800 flex flex-wrap gap-1">
                                    {kpiColumns?.map((kc: string) => (
                                        <span key={kc} className="bg-[#F5E003] px-2 py-0.5 rounded-full">
                                            {kc}
                                        </span>
                                    ))}
                                </div>
                                {availableKpi.length > 0 && (
                                    <div className="mt-1">
                                        <div className="text-[10px] text-gray-400">{t('Available')}:</div>
                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                            {availableKpi.map((col: string) => (
                                                <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                                    {col}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                            {/* OBS-only: Group Column + Control Group Label */}
                            {isObs && (
                                <div>
                                    <div className="text-[11px] text-gray-500">
                                        {t('Grouping Column')} : <i>{t('column that maps Test and Control groups')}</i>
                                    </div>
                                    <div className="text-[11px] font-semibold text-gray-800">{selected.group_column ?? '—'}</div>
                                    {availableGroupColumn.length > 0 && (
                                        <div className="mt-1">
                                            <div className="text-[10px] text-gray-400">{t('Available')}:</div>
                                            <div className="flex flex-wrap gap-1 mt-0.5">
                                                {availableGroupColumn?.map((col: string) => (
                                                    <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                                        {col}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                                {isObs && (
                                    <div>
                                        <div className="text-[11px] text-gray-500">{t('Control Group Value')}</div>
                                        <div className="text-[11px] font-semibold text-gray-800 mt-0.5">
                                            <span>{selected.control_group_label ?? '—'}</span>
                                        </div>
                                    </div>
                                )}
                                {isObs && (
                                    <div>
                                        <div className="text-[11px] text-gray-500">{t('Test Group Value')}</div>
                                        <div className="text-[11px] font-semibold text-gray-800 mt-0.5">
                                            <span>{selected.treatment_group_label ?? '—'}</span>
                                        </div>
                                        {(availableColumns?.treatment_group || []).length > 0 && (
                                            <div className="mt-1">
                                                <div className="text-[10px] text-gray-400">{t('Available')}:</div>
                                                <div className="flex flex-wrap gap-1 mt-0.5">
                                                    {(availableColumns?.treatment_group || []).map((tgl: string) => (
                                                        <span key={tgl} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                                            {tgl}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {availableTestControlValues.length > 0 && (
                                    <div className="col-span-2">
                                        <div className="text-[10px] text-gray-400">
                                            {t('Available values in')} <span className="font-semibold text-gray-800">{selected.group_column}</span>:
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-0.5">
                                            {availableTestControlValues.map((col: string) => (
                                                <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                                    {col}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* OBS-only: Features */}
                {isObs && (
                    <div className="mt-3">
                        <div className="text-xs font-semibold text-gray-800 mb-1.5">{t('Features')}</div>
                        <div className="flex flex-wrap gap-1.5">
                            {features?.map((f: string) => (
                                <span key={f} className="bg-[#FDFAEE] border border-[#E8DFB8] text-[11px] text-gray-700 px-2 py-0.5 rounded-full">
                                    {f}
                                </span>
                            ))}
                        </div>
                        {availableFeatures?.length > 0 && (
                            <div className="mt-1.5">
                                <div className="text-[10px] text-gray-400">{t('Available')}:</div>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                    {availableFeatures.map((col: string) => (
                                        <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                            {col}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* RCT-only: Blocking Factors */}
                {!isObs && (
                    <div className="mt-3">
                        <div className="text-xs font-semibold text-gray-800 mb-1.5">{t('Blocking Factors')}</div>
                        <div className="flex flex-wrap gap-1.5">
                            {blockingFactors?.map((bf: string) => (
                                <span key={bf} className="bg-[#FDFAEE] border border-[#E8DFB8] text-[11px] text-gray-700 px-2 py-0.5 rounded-full">
                                    {bf}
                                </span>
                            ))}
                        </div>
                        {availableBlocking?.length > 0 && (
                            <div className="mt-1.5">
                                <div className="text-[10px] text-gray-400">{t('Available')}:</div>
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                    {availableBlocking.map((col: string) => (
                                        <span key={col} className="bg-gray-100 text-[10px] text-gray-600 px-1.5 py-0.5 rounded">
                                            {col}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* RCT-only: Group Distributions */}
                {!isObs && groupDistributions.length > 0 && (
                    <div className="mt-3">
                        <div className="text-xs font-semibold text-gray-800 mb-1.5">{t('Group Distributions')}</div>
                        <div className="grid grid-cols-2 gap-3">
                            {groupDistributions.map((g: any, idx: number) => {
                                const isControl = g.is_control ?? idx === 0
                                const hasSplitData = g.distribution !== undefined || g.sample_size !== undefined
                                return (
                                    <div key={g.name} className={`flex items-center justify-between px-2.5 py-2 rounded-md ${isControl ? 'bg-black text-white' : 'bg-[#F5E003] text-gray-900'}`}>
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[11px] font-semibold">{g.name}</span>
                                                {isControl && <span className="bg-[#4B471B] text-[#F5E003] text-[9px] px-1.5 rounded-full">{t('Control')}</span>}
                                            </div>
                                            {hasSplitData ? (
                                                <div className="flex gap-2 text-[10px]">
                                                    <div>
                                                        <div className={isControl ? 'text-slate-400' : 'text-gray-600'}>{t('Distribution')} :</div>
                                                        <div className={`font-medium ${isControl ? 'text-[#F5E003]' : ''}`}>{g.distribution}%</div>
                                                    </div>
                                                </div>
                                            ) : (
                                                g.action && (
                                                    <div className="flex gap-2 text-[10px]">
                                                        <div>
                                                            <div className={isControl ? 'text-slate-400' : 'text-gray-600'}>{t('Action')} :</div>
                                                            <div className={`font-medium ${isControl ? 'text-[#F5E003]' : ''}`}>{g.action}</div>
                                                        </div>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                        {hasSplitData && (
                                            <div className="flex flex-col items-end text-[10px]">
                                                <span className={isControl ? 'text-slate-400' : 'text-gray-500'}>{t('Sample Size')}</span>
                                                <span className="text-base font-bold">{g.sample_size?.toLocaleString()}</span>
                                                {totalSampleSize && (
                                                    <span className={isControl ? 'text-slate-400' : 'text-gray-500'}>
                                                        {t('out of')} <span className={isControl ? 'text-slate-200' : 'font-semibold'}>{totalSampleSize.toLocaleString()}</span>
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

const TypingLoadingText: FC<{ text: string }> = ({ text }) => {
    const [displayed, setDisplayed] = useState('')
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const startTyping = useCallback((str: string) => {
        if (intervalRef.current) clearInterval(intervalRef.current)
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        setDisplayed('')
        let i = 0
        intervalRef.current = setInterval(() => {
            i++
            if (i >= str.length) {
                i = str.length
                if (intervalRef.current) clearInterval(intervalRef.current)
                intervalRef.current = null
                timeoutRef.current = setTimeout(() => {
                    startTyping(str)
                }, 3000)
            }
            setDisplayed(str.slice(0, i))
        }, 30)
    }, [])

    useEffect(() => {
        if (text) startTyping(text)
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
        }
    }, [text, startTyping])

    return <p className="text-xs text-gray-700">&nbsp;{displayed}</p>
}

const ChatToExperiment: FC<ChatToExperimentProps> = ({ _ref, initialSteps = null, initialMessages, initialSessionId, onSendMessage, onUploadFile }) => {
    const { t } = useTranslation()
    const user: any = useUserSelector()
    const language = useLanguageSelector()
    const chatToExperimentAPI = useChatToExperimentApi()
    const experimentAPI: any = useExperimentApi()
    const [steps, setSteps] = useState<Step[] | null>(initialSteps)
    const [messages, setMessages] = useState<Message[]>(initialMessages || [])
    const [inputValue, setInputValue] = useState('')
    const [sessionId, setSessionId] = useState<string | null>(null)
    const [isLoadingHistory, setIsLoadingHistory] = useState(true)
    const [isSending, setIsSending] = useState(false)
    const [typingMessageId, setTypingMessageId] = useState<string | null>(null)
    const [displayedText, setDisplayedText] = useState('')
    const [supportNote, setSupportNote] = useState<string>('')
    const fullTextRef = useRef('')
    const typingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const { transcript, isListening, startListening, stopListening, setTranscript } = useSpeechToText({ continuous: true })

    const getUserDisplayName = () => {
        const firstName = user?.first_name || ''
        const lastName = user?.last_name || ''
        const zone = user?.zones?.[0]?.title || user?.zones?.[0]?.zone_literal || ''
        const namePart = lastName && firstName ? `${lastName}, ${firstName}` : firstName || lastName || 'User'
        return zone ? `${namePart}` : namePart
    }

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages, displayedText])

    useEffect(() => {
        const display = transcript.replaceAll('\u200B', '')
        setInputValue(display)
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto'
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 240)}px`
        }
    }, [transcript])

    const startTypingEffect = useCallback((msgId: string, fullText: string) => {
        if (typingIntervalRef.current) clearInterval(typingIntervalRef.current)
        fullTextRef.current = fullText
        setTypingMessageId(msgId)
        setDisplayedText('')
        let charIndex = 0
        const len = fullText.length
        const charsPerTick = getCharsPerTick(len)
        typingIntervalRef.current = setInterval(() => {
            charIndex += charsPerTick
            if (charIndex >= len) charIndex = len
            setDisplayedText(fullTextRef.current.slice(0, charIndex))
            if (charIndex >= len) {
                if (typingIntervalRef.current) clearInterval(typingIntervalRef.current)
                typingIntervalRef.current = null
                setTypingMessageId(null)
            }
        }, 15)
    }, [])

    const addLoadingTextToLastMessage = (data: any) => {
        setMessages((prev) => {
            const lastMessage = prev.at(-1)
            if (!lastMessage) return prev
            const updated: Message = { ...lastMessage, loadingText: data?.description }
            return [...prev.slice(0, -1), updated]
        })
    }

    const addPusherEvents = () => {
        pusherService.bind('tool_start', addLoadingTextToLastMessage)
        pusherService.bind('tool_result', addLoadingTextToLastMessage)
        return () => {
            pusherService.unbind('tool_start', addLoadingTextToLastMessage)
            pusherService.unbind('tool_result', addLoadingTextToLastMessage)
        }
    }

    const getSupportNote = () => {
        chatToExperimentAPI.getSupportNote(
            {},
            (resp: any) => {
                setSupportNote(resp.data?.support_note || '')
            },
            () => {
                setSupportNote('')
            },
        )
    }

    useEffect(() => {
        getSupportNote()
        addPusherEvents()
        return () => {
            if (typingIntervalRef.current) clearInterval(typingIntervalRef.current)
        }
    }, [])

    const loadSessionMessages = (sid: string) => {
        chatToExperimentAPI.getSessionMessages(
            { session_id: sid },
            (resp: any) => {
                const history = resp.data?.messages || []
                const filteredHistory = history.filter((m: any) => m.timestamp || m.text || m.response) // filter out empty messages
                if (filteredHistory.length > 0) {
                    const restored: Message[] = filteredHistory.map((m: any, idx: number) => ({
                        id: `history_${idx}`,
                        text: m.content || m.text || m.response || '',
                        sender: m.role === 'user' ? 'user' : 'bot',
                        timestamp: m.timestamp || '',
                        uiPayload: m.ui_payload || undefined,
                        buttons: m.buttons || undefined,
                    }))
                    setMessages(restored)

                    // Restore steps from the last bot message that has them
                    const lastBotWithSteps = [...filteredHistory].reverse().find((m: any) => m.role !== 'user' && m.steps)
                    if (lastBotWithSteps?.steps) {
                        setSteps(lastBotWithSteps.steps)
                    }
                }
                trackEvent('ai_session_messages_loaded', { session_id: sid, message_count: filteredHistory.length })
                setIsLoadingHistory(false)
            },
            () => {
                trackEvent('ai_session_messages_load_failed', { session_id: sid })
                setIsLoadingHistory(false)
            },
        )
    }

    const createSession = () => {
        setSessionId(null)
        chatToExperimentAPI.createSession(
            { language },
            (resp: any) => {
                const newSessionId = resp.data.session_id
                trackEvent('ai_session_created', { session_id: newSessionId })
                setSessionId(newSessionId)
                setMessages([])
                setIsLoadingHistory(false)
                setSteps(null)
            },
            () => {
                trackEvent('ai_session_create_failed', {})
                setIsLoadingHistory(false)
            },
        )
    }

    const handleSend = (message?: string) => {
        const text = message || inputValue
        if (!text.trim() || isSending) return
        setIsSending(true)
        trackEvent('ai_message_sent', { message_length: text.length, session_id: sessionId })
        onSendMessage?.(text)
        const newMsg: Message = {
            id: Date.now().toString(),
            text,
            sender: 'user',
            timestamp: Date.now(),
        }
        const loadingMsgId = (Date.now() + 1).toString()
        const loadingMsg: Message = {
            id: loadingMsgId,
            text: '',
            sender: 'bot',
            timestamp: Date.now(),
            isLoading: true,
            loadingText: t('Thinking...'),
        }
        setMessages((prev) => [...prev, newMsg, loadingMsg])
        setInputValue('')
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto'
        }
        chatToExperimentAPI.chat(
            { message: text, session_id: sessionId, language },
            (resp: any) => {
                const newSteps = resp.data?.steps || null
                if (newSteps) {
                    // Track individual step status changes
                    if (steps) {
                        newSteps.forEach((newStep: Step, index: number) => {
                            const oldStep = steps[index]
                            if (oldStep && oldStep.status !== newStep.status) {
                                trackEvent('ai_step_status_changed', {
                                    step_id: newStep.id,
                                    step_label: newStep.label,
                                    from_status: oldStep.status,
                                    to_status: newStep.status,
                                    session_id: sessionId,
                                })
                            }
                        })
                    }
                    // Track overall progress summary
                    const completedCount = newSteps.filter((s: Step) => s.status === 'completed').length
                    const activeStep = newSteps.find((s: Step) => s.status === 'active')
                    trackEvent('ai_step_progress_updated', {
                        completed_count: completedCount,
                        total_steps: newSteps.length,
                        active_step_label: activeStep?.label || null,
                        session_id: sessionId,
                    })
                    setSteps(newSteps)
                }

                const responseText = resp.data?.response || resp.data?.content || "Sorry, I didn't understand that."
                const uiPayload = resp.data?.ui_payload || undefined
                const buttons = resp.data?.buttons || undefined
                trackEvent('ai_bot_response_received', {
                    ui_payload_type: uiPayload?.type,
                    has_buttons: !!buttons,
                    has_steps: !!newSteps,
                })
                if (resp.data?.experiment_created && resp.data?.experiment_literal) {
                    // dim for created-by-chat vs created-by-product dashboards
                    trackEvent('experiment_created', {
                        experiment_id: resp.data.experiment_literal,
                        source: 'chat_to_experiment',
                        session_id: sessionId,
                    })
                }
                const botMsgId = Date.now().toString()
                setMessages((prev) => replaceMessage(prev, loadingMsgId, { id: botMsgId, text: responseText, isLoading: false, uiPayload, buttons }))
                startTypingEffect(botMsgId, responseText)
                setIsSending(false)
            },
            (err) => {
                trackEvent('ai_bot_response_failed', { session_id: sessionId })
                const errorText = 'Sorry, something went wrong'
                setMessages((prev) => replaceMessage(prev, loadingMsgId, { text: errorText, isLoading: false, buttons: { [t('Try Again')]: text } }))
                setIsSending(false)
            },
        )
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        if (file && !file.name.toLowerCase().endsWith('.csv') && !file.name.toLowerCase().endsWith('.xlsx')) {
            e.target.value = ''
            e.target.setCustomValidity('Only CSV and XLSX files are accepted')
            e.target.reportValidity()
            e.target.setCustomValidity('')
            return
        }
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(0)
        const ext = file.name.split('.').pop()?.toUpperCase() || ''
        const botResponse: Message = {
            id: Date.now().toString(),
            text: 'Want to continue to the next steps?\n\n1. Do you want to edit current step?\n2. Proceed to next step?',
            sender: 'bot',
            timestamp: formatTime(),
            attachment: { name: file.name, size: `${fileSizeMB}MB`, type: ext },
        }
        setMessages((prev) => [...prev, botResponse])
        onUploadFile?.(file)
    }

    const getExperimentDetails = (experimentLiteral: string) => {
        return new Promise((resolve, reject) => {
            experimentAPI.getExperimentDetailsWatch(
                { literal: experimentLiteral + '?use_orm=true' },
                (resp: any) => {
                    resolve(resp)
                },
                (error: any) => {
                    toast.error(error?.message || 'Failed to fetch experiment details')
                    reject(error)
                },
            )
        })
    }

    const exportRctGroupResultS = async (experimentLiteral: any) => {
        const experiment: any = await getExperimentDetails(experimentLiteral)

        const hasGzipFile = experiment?.experiment_metadatas?.design_data_quality_report?.output_gzip_file_name
        const fileName: string = experiment?.experiment_type?.title == 'Observational Study' ? `${experimentLiteral}_Matching_Result.csv` : `${experimentLiteral}_RCT_Result.csv`
        if (hasGzipFile) {
            experimentAPI.downloadRctGZipResults(
                { literal: experimentLiteral },
                (resp: any) => {
                    decompressAndDownload(resp, fileName).catch(() => {
                        toast.error(t('Failed to decompress file. Please try again.'))
                    })
                },
                (err: any) => {
                    toast.error(err?.message || t('Failed to download files. Please try again later.'))
                },
            )
        } else {
            experimentAPI.downloadrctresult(
                {
                    literal: experimentLiteral,
                },
                (resp: any) => {
                    const csvContent = resp
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
                    const url = URL.createObjectURL(blob)
                    const link = document.createElement('a')
                    link.href = url
                    link.download = fileName
                    document.body.appendChild(link)
                    link.click()
                    link.remove()
                    URL.revokeObjectURL(url)
                },
                (err: any) => {
                    toast.error(err?.message || t('Failed to download files. Please try again later.'))
                },
            )
        }
    }

    useEffect(() => {
        if (initialMessages) {
            if (initialSessionId) {
                setSessionId(initialSessionId)
            }
            setIsLoadingHistory(false)
            return
        }
        if (initialSessionId) {
            setSessionId(initialSessionId)
            loadSessionMessages(initialSessionId)
            return
        }
        chatToExperimentAPI.listSessions(
            {},
            (resp: any) => {
                const sessions = resp.data?.result || []
                trackEvent('ai_sessions_listed', { session_count: sessions.length, auto_resumed: sessions.length > 0 })
                if (sessions.length > 0) {
                    const latestSession = sessions[0]
                    const sid = latestSession.session_id
                    setSessionId(sid)
                    loadSessionMessages(sid)
                } else {
                    createSession()
                }
            },
            () => {
                trackEvent('ai_sessions_list_failed', { fallback: 'create_new' })
                createSession()
            },
        )
    }, [])

    useEffect(() => {
        if (_ref?.current) {
            _ref.current.createNewSession = createSession
        }
    }, [_ref.current])

    return (
        <div className="flex flex-col flex-grow overflow-hidden bg-[#F8F8F8]" ref={_ref}>
            {sessionId && !isLoadingHistory ? (
                <div className="flex h-full font-sans  w-full">
                    {/* Chat Area */}
                    <div className="flex flex-col bg-white flex-grow min-w-0">
                        {/* Messages */}
                        <div className="overflow-y-auto px-6 py-5">
                            <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
                                <div className="max-w-[96%] w-full text-left">
                                    <h1 className="text-xl font-bold text-gray-900 m-0 leading-snug">
                                        {t('Hi')} <span className="text-[#d4a017] font-bold">{getUserDisplayName()},</span>
                                        <br />
                                        {/* Welcome to TestOps */}
                                    </h1>
                                    <p className="text-xs text-gray-500 leading-relaxed mt-3 mb-8">
                                        {t('I help you design and run new experiments end-to-end through conversation.')}
                                        <br />
                                        {t('I will handle the setup, you make the decisions.')}
                                    </p>
                                    <div className="flex gap-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                trackEvent('ai_quick_action_clicked', { action: 'create_new_experiment' })
                                                handleSend(t('Create new experiment'))
                                            }}
                                            className="flex-1 flex items-center justify-between p-2 bg-white border border-[#D7D7D7] rounded-[0.33rem] cursor-pointer text-left transition-shadow hover:shadow-md"
                                        >
                                            <div className="flex items-start gap-2">
                                                <PiBookOpenBold className="text-[#475569] text-[1.3rem]" />
                                                <div>
                                                    <div className="font-semibold text-xs text-gray-900 mb-1">{t('Create new experiment')}</div>
                                                    <div className="text-[0.75rem] text-gray-400">{t('Set up and run experiments in just a few clicks')}</div>
                                                </div>
                                            </div>
                                            <span className="text-lg text-gray-300 ml-3">→</span>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                trackEvent('ai_quick_action_clicked', { action: 'continue_existing' })
                                                handleSend(t('Continue with existing experiments'))
                                            }}
                                            className="flex-1 flex items-center justify-between p-2 bg-white border border-[#D7D7D7] rounded-[0.33rem] cursor-pointer text-left transition-shadow hover:shadow-md"
                                        >
                                            <div className="flex items-start gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 16 16" fill="none" style={{ fontSize: '1.2rem' }} aria-hidden="true">
                                                    <rect width="16" height="16" fill="none" />
                                                    <path
                                                        d="M5 14C4.86739 14 4.74021 13.9473 4.64645 13.8536C4.55268 13.7598 4.5 13.6326 4.5 13.5V2.5C4.50002 2.41219 4.52317 2.32594 4.56711 2.24992C4.61105 2.1739 4.67423 2.11079 4.7503 2.06694C4.82638 2.02309 4.91266 2.00004 5.00046 2.00013C5.08827 2.00021 5.17451 2.02341 5.2505 2.0674L14.7505 7.5674C14.8263 7.61135 14.8892 7.67444 14.9329 7.75034C14.9766 7.82625 14.9996 7.91231 14.9996 7.9999C14.9996 8.0875 14.9766 8.17355 14.9329 8.24946C14.8892 8.32536 14.8263 8.38845 14.7505 8.4324L5.2505 13.9324C5.17443 13.9766 5.088 14 5 14ZM5.5 3.3672V12.6328L13.5 8L5.5 3.3672Z"
                                                        fill="currentColor"
                                                    />
                                                    <path d="M3 2H2V14H3V2Z" fill="currentColor" />
                                                </svg>
                                                <div>
                                                    <div className="font-semibold text-xs text-gray-900 mb-1">{t('Continue with existing experiments')}</div>
                                                    <div className="text-[0.75rem] text-gray-400">{t('Resume your pending experiments')}</div>
                                                </div>
                                            </div>
                                            <span className="text-lg text-gray-300 ml-3">→</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {messages.map((msg, msgIndex) => {
                                const lastBotIndex = messages.reduce((acc, m, i) => (m.sender === 'bot' && !m.isLoading ? i : acc), -1)
                                const isLatestBotMessage = msg.sender === 'bot' && msgIndex === lastBotIndex
                                return (
                                    <div key={msg.id} className={`flex mb-4 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                        <div className="w-[85%] ">
                                            {msg.sender === 'bot' && (
                                                <div className="flex items-start gap-2 w-full">
                                                    <div className="flex-shrink-0">{msg.isLoading ? <LogoIcon className="w-4 h-4 animate-spin" /> : <img src="/assets/images/Genie.svg" alt="User Avatar" className="w-5 h-5 rounded-full" />}</div>
                                                    <div className="w-full">
                                                        {msg.attachment && (
                                                            <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl mb-2 border border-gray-100">
                                                                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center text-xl">📄</div>
                                                                <div>
                                                                    <div className="font-semibold text-xs">{msg.attachment.name}</div>
                                                                    <div className="text-xs text-gray-400">
                                                                        {msg.attachment.size} . {msg.attachment.type} . Just Now
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        {msg.isLoading ? (
                                                            <div className="flex items-baseline">
                                                                <TypingLoadingText text={msg.loadingText || t('Thinking...')} />
                                                            </div>
                                                        ) : (
                                                            <div className="text-xs relative leading-relaxed text-gray-700">
                                                                {msg.uiPayload?.type === 'summary_card' && typingMessageId !== msg.id && <SummaryCard data={msg.uiPayload.data} />}
                                                                {(msg.uiPayload?.type === 'data_preview_for_design' || msg.uiPayload?.type === 'data_preview_for_measurement') && typingMessageId !== msg.id && (
                                                                    <DataPreviewCard data={msg.uiPayload.data} />
                                                                )}
                                                                {msg.uiPayload?.type === 'applied_sql' && typingMessageId !== msg.id && <AppliedSqlCard data={msg.uiPayload.data} />}
                                                                {msg.uiPayload?.type === 'measurement_config' && typingMessageId !== msg.id && <MeasurementConfigCard data={msg.uiPayload.data} />}
                                                                {msg.uiPayload?.type === 'group_config' && typingMessageId !== msg.id && <GroupConfigCard data={msg.uiPayload.data} />}
                                                                {msg.uiPayload?.type === 'data_upload_for_design' &&
                                                                    typingMessageId !== msg.id &&
                                                                    (() => {
                                                                        const uploadUrl = msg.uiPayload?.data?.upload_url || ''
                                                                        const urlPath = uploadUrl.split('/create-experiment/')[1] || ''
                                                                        const expLiteral = urlPath.split('?')[0]
                                                                        return expLiteral ? (
                                                                            <div className="my-2 border border-gray-200 rounded-xl overflow-hidden relative">
                                                                                <UploadData experimentLiteral={expLiteral} onComplete={(message) => handleSend(message)} hidePreviousStep />
                                                                            </div>
                                                                        ) : null
                                                                    })()}
                                                                {msg.uiPayload?.type === 'filter_for_design' &&
                                                                    typingMessageId !== msg.id &&
                                                                    (() => {
                                                                        const filterUrl = msg.uiPayload?.data?.filter_url || ''
                                                                        const urlPath = filterUrl.split('/create-experiment/')[1] || ''
                                                                        const expLiteral = urlPath.split('?')[0]
                                                                        return expLiteral ? (
                                                                            <div className="my-2 p-4 border border-gray-200 rounded-xl overflow-hidden relative">
                                                                                <ProcessData experimentLiteral={expLiteral} onComplete={(message) => handleSend(message)} hidePreviousStep />
                                                                            </div>
                                                                        ) : null
                                                                    })()}
                                                                {msg.uiPayload?.type === 'data_upload_for_measurement' &&
                                                                    typingMessageId !== msg.id &&
                                                                    (() => {
                                                                        const uploadUrl = msg.uiPayload?.data?.upload_url || ''
                                                                        const match = uploadUrl.match(/\/measurement\/([^/?]+)/)
                                                                        const expLiteral = match?.[1] || ''
                                                                        return expLiteral ? (
                                                                            <div className="my-2 border border-gray-200 rounded-xl overflow-hidden relative">
                                                                                <RCTUploadData experimentLiteral={expLiteral} onComplete={(message) => handleSend(message)} hidePreviousStep next={() => {}} />
                                                                            </div>
                                                                        ) : null
                                                                    })()}
                                                                {msg.uiPayload?.type === 'filter_for_measurement' &&
                                                                    typingMessageId !== msg.id &&
                                                                    (() => {
                                                                        const filterUrl = msg.uiPayload?.data?.filter_url || ''
                                                                        const match = filterUrl.match(/\/measurement\/([^/?]+)/)
                                                                        const expLiteral = match?.[1] || ''
                                                                        return expLiteral ? (
                                                                            <div className="my-2 p-4 border border-gray-200 rounded-xl overflow-hidden relative">
                                                                                <MeasurementProcessData
                                                                                    experimentLiteral={expLiteral}
                                                                                    onComplete={(message) => handleSend(message)}
                                                                                    hidePreviousStep
                                                                                    dataType="Measurement"
                                                                                    filterKey="measurement_table_filters"
                                                                                    conversionFlow={false}
                                                                                    next={() => {}}
                                                                                    prev={() => {}}
                                                                                    _for={null}
                                                                                />
                                                                            </div>
                                                                        ) : null
                                                                    })()}
                                                                <Md text={typingMessageId === msg.id ? displayedText : msg.text} />
                                                                {typingMessageId === msg.id && <span className="inline-block w-0.5 h-4 bg-gray-500 align-middle ml-0.5 animate-pulse" />}
                                                                {msg.uiPayload?.type === 'design_actions' && typingMessageId !== msg.id && (
                                                                    <div className={`flex gap-2 mt-3 flex-wrap ${isLatestBotMessage ? '' : 'opacity-50 pointer-events-none'}`}>
                                                                        {msg.uiPayload.data.results_url && (
                                                                            <a
                                                                                href={msg.uiPayload.data.results_url}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                onClick={() => trackEvent('ai_view_rct_results_clicked', { url: msg.uiPayload?.data?.results_url })}
                                                                                className="px-3 py-1.5 rounded-full border border-[#CD9F27] bg-white text-[#CD9F27] text-xs font-semibold cursor-pointer no-underline hover:bg-[#FDFAEE] transition-colors"
                                                                            >
                                                                                View Design
                                                                            </a>
                                                                        )}
                                                                        {msg.uiPayload.data.download_url && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    trackEvent('ai_download_rct_clicked', { url: msg.uiPayload?.data?.download_url })
                                                                                    const literal = msg.uiPayload?.data?.download_url?.split('?')[0].split('/').pop() || ''
                                                                                    if (literal) {
                                                                                        // getExperimentDetails already shows a toast on failure.
                                                                                        exportRctGroupResultS(literal).catch((error) => {
                                                                                            console.error('Failed to export RCT group results:', error)
                                                                                        })
                                                                                    } else {
                                                                                        window.open(msg.uiPayload?.data?.download_url, '_blank', 'noopener,noreferrer')
                                                                                    }
                                                                                }}
                                                                                className="px-3 py-1.5 rounded-full border border-gray-300 bg-white text-gray-700 text-xs font-semibold cursor-pointer no-underline hover:bg-gray-50 transition-colors"
                                                                            >
                                                                                Download Design
                                                                            </button>
                                                                        )}
                                                                        {msg.uiPayload.data.submit_experiment && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    trackEvent('ai_submit_experiment_clicked', { session_id: sessionId })
                                                                                    handleSend(`Submit experiment`)
                                                                                }}
                                                                                disabled={!isLatestBotMessage}
                                                                                className="px-3 py-1.5 rounded-full border-none bg-[#F5E003] text-gray-900 text-xs font-semibold cursor-pointer hover:bg-[#e6d303] transition-colors disabled:cursor-not-allowed"
                                                                            >
                                                                                Submit Experiment
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {msg.uiPayload?.type === 'analysis_triggered' && typingMessageId !== msg.id && (
                                                                    <div className={`flex gap-2 mt-3 flex-wrap ${isLatestBotMessage ? '' : 'opacity-50 pointer-events-none'}`}>
                                                                        {msg.uiPayload.data.experiment_url && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    trackEvent('ai_submit_experiment_clicked', { session_id: sessionId })
                                                                                    window.open(msg.uiPayload?.data?.experiment_url, '_blank', 'noopener,noreferrer')
                                                                                }}
                                                                                disabled={!isLatestBotMessage}
                                                                                className="px-3 py-1.5 rounded-full border-none bg-[#F5E003] text-gray-900 text-xs font-semibold cursor-pointer hover:bg-[#e6d303] transition-colors disabled:cursor-not-allowed"
                                                                            >
                                                                                View Experiment Insight
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {msg.uploadButton && (
                                                            <button
                                                                type="button"
                                                                onClick={() => fileInputRef.current?.click()}
                                                                disabled={!isLatestBotMessage}
                                                                className={`mt-2 px-4 py-2 rounded-full border border-gray-300 bg-white cursor-pointer text-[13px] flex items-center gap-1.5 ${isLatestBotMessage ? '' : 'opacity-50 pointer-events-none'}`}
                                                            >
                                                                📎 Upload a data set
                                                            </button>
                                                        )}
                                                        {msg.buttons && typingMessageId !== msg.id && (
                                                            <div className={`flex gap-2 mt-3 flex-wrap ${isLatestBotMessage ? '' : 'opacity-50 pointer-events-none'}`}>
                                                                {Object.entries(msg.buttons).map(([label, value]) => (
                                                                    <button
                                                                        key={label}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            trackEvent('ai_bot_action_button_clicked', { label, value })
                                                                            handleSend(value)
                                                                        }}
                                                                        disabled={!isLatestBotMessage}
                                                                        className="px-3 py-1.5 rounded-full border border-[#CD9F27] bg-white text-[#CD9F27] text-xs font-semibold cursor-pointer hover:bg-[#FDFAEE] transition-colors disabled:cursor-not-allowed"
                                                                    >
                                                                        {label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <div className="text-[11px] text-gray-300 mt-1">{moment(msg.timestamp).fromNow()}</div>
                                                    </div>
                                                </div>
                                            )}
                                            {msg.sender === 'user' && (
                                                <div className="flex  gap-2 w-full justify-end">
                                                    <div>
                                                        <div className="bg-gray-100 px-4 py-2.5 rounded-2xl text-xs text-gray-700 whitespace-pre-wrap break-words">{msg.text}</div>
                                                        <div className="text-[0.5rem] text-gray-300 mt-1 text-right">{moment(msg.timestamp).fromNow()}</div>
                                                    </div>
                                                    <div className="w-5 h-5 text-[0.6rem] flex items-center justify-center font-semibold bg-primary text-secondary rounded-full">
                                                        {user?.first_name?.charAt(0)}
                                                        {user?.last_name?.charAt(0)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input */}
                        <div className="px-6 pt-3 pb-5 border-t border-gray-100">
                            <div className="flex items-end gap-1 bg-gray-50 rounded-3xl px-4 py-2 border border-gray-100">
                                <textarea
                                    ref={textareaRef}
                                    value={inputValue}
                                    onChange={(e) => {
                                        setInputValue(e.target.value)
                                        e.target.style.height = 'auto'
                                        e.target.style.height = `${Math.min(e.target.scrollHeight, 240)}px`
                                    }}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t('Write a follow-up question')}
                                    className="flex-1 border-none outline-none bg-transparent text-sm py-1 resize-none overflow-y-auto max-h-[240px]"
                                    rows={1}
                                />
                                {APP_ENV !== 'production' && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (isListening) {
                                                trackEvent('ai_chat_mic_stopped', { transcript_length: transcript?.length ?? 0 })
                                                stopListening()
                                            } else {
                                                trackEvent('ai_chat_mic_started', {})
                                                setTranscript(inputValue + ' ')
                                                startListening()
                                            }
                                        }}
                                        className={`w-8 h-8 rounded-full cursor-pointer flex items-center justify-center text-base transition ${isListening ? 'bg-red-600 text-white scale-110 animate-pulse shadow-lg border-red-600' : 'hover:bg-yellow-500 text-slate-500 hover:text-white border-[#CBD5E1]'}`}
                                    >
                                        <IoMdMic />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleSend()
                                    }}
                                    disabled={!inputValue.trim() || isSending}
                                    className={`w-8 h-8 rounded-full border-none cursor-pointer flex items-center justify-center text-base text-white disabled:cursor-not-allowed ${inputValue.trim() && !isSending ? 'bg-[#f5a623]' : 'bg-gray-200'}`}
                                >
                                    <VscSend />
                                </button>
                            </div>
                            <p className="text-start pl-4 text-[0.52rem] text-slate-300 leading-[1.4] italic">{supportNote}</p>
                        </div>

                        <input ref={fileInputRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleFileUpload} />
                    </div>

                    {/* Sidebar */}
                    {steps && (
                        <div className="w-[260px] flex-shrink-0 border-l border-gray-100 px-5 py-6 bg-white h-full overflow-y-scroll">
                            <p className="text-xs text-gray-400 mb-5">{t('Follow each step in sequence to complete the process.')}</p>
                            {steps.map((step, stepIndex) => (
                                <div key={step.id} className="flex gap-3 mb-5 relative">
                                    {stepIndex < steps.length - 1 && <div className={`absolute left-[13px] top-7 border-l w-0 h-[calc(100%-3px)] ${getStepLineClass(step.status)}`} />}
                                    <div className="text-lg flex-shrink-0 pt-2 pl-1">
                                        <PiCheckCircleFill className={getStepIconClass(step.status)} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-[13px] mt-1 text-gray-700">{t(STEP_LABEL_KEYS[step.id] ?? step.label)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex items-center justify-center h-full w-full">
                    <Loader />
                </div>
            )}
        </div>
    )
}

export default ChatToExperiment
